// Money brought from Squeegee, credit and cash taken (0043; docs/plans/moving-in-with-checks/PLAN.md sections 2 and 6,
// Liam's answers 2 Oct 2026), as the phone shows it. Pure. Every figure here comes from the server (functions/lib/
// money.ts); a visit on a round without brought money carries none of the fields, and every function below then
// answers exactly what the screens said before. No imports: the words take the screen's own money formatter (ui.money),
// so the pure modules that use this (customer-analysis.ts, payment.ts) stay free of the DOM.

export type Fmt = (pence: number) => string

export interface VisitMoney {
  amount_pence: number | null
  state?: string
  payment_status?: string | null
  payment_method?: string | null
  carryover?: 1 | null
  from_credit_pence?: number
  paid_in_pence?: number
  cash_taken_pence?: number
  cash_unconfirmed?: number
  // Today only: what the customer still owes on accounts brought from Squeegee, for the door.
  account_owed_pence?: number
  // The customer's credit is still being set aside (the server could not finish it yet, Astra r4 finding 4): their
  // figures are not ready to collect from.
  money_pending?: number
}

export const moneyPending = (v: VisitMoney): boolean => (v.money_pending ?? 0) > 0
export const PENDING_WORDS = 'Credit still being worked out: check the price with the owner before collecting'

// "Also owes £12 from Squeegee" on a Today card, or null.
export const accountOwed = (v: VisitMoney): number => Math.max(0, v.account_owed_pence ?? 0)
export function alsoOwesWords(v: VisitMoney, money: Fmt): string | null {
  return accountOwed(v) ? `Also owes ${money(accountOwed(v))} from Squeegee` : null
}

// The Done panel's line and the cash it starts on: the visit's to-collect plus what is owed from Squeegee.
export function doorWords(v: VisitMoney, money: Fmt): { line: string; takePence: number | null } | null {
  // Not ready: said, and Cash starts empty rather than on a figure that may be wrong.
  if (moneyPending(v)) return { line: PENDING_WORDS, takePence: null }
  const credit = fromCredit(v)
  const owed = accountOwed(v)
  if (!credit && !owed) return null
  const due = toCollect(v) ?? 0
  const parts = [credit ? `${money(credit)} from credit · ${money(due)} to collect` : `${money(due)} for this clean`]
  if (owed) parts.push(`+ ${money(owed)} from Squeegee = ${money(due + owed)}`)
  return { line: parts.join(' '), takePence: due + owed }
}

// Credit on a visit (set aside, or used at Done).
export const fromCredit = (v: VisitMoney): number => Math.max(0, v.from_credit_pence ?? 0)

// What is still to collect at the door: the price, less credit and anything already paid in. The price when the
// visit carries no money.
export function toCollect(v: VisitMoney): number | null {
  if (v.amount_pence === null || v.amount_pence === undefined) return null
  return Math.max(0, v.amount_pence - fromCredit(v) - Math.max(0, v.paid_in_pence ?? 0))
}

// "£24 · £5 from credit · £19 to collect" (Liam Q1), or null when no credit is on the visit.
export function creditWords(v: VisitMoney, money: Fmt): string | null {
  const credit = fromCredit(v)
  if (!credit || v.amount_pence === null) return null
  return `${money(v.amount_pence)} · ${money(credit)} from credit · ${money(toCollect(v) ?? 0)} to collect`
}

// Real money received for a done visit: cash or bank paid in on it, else (a visit marked paid with nothing recorded on
// it, as every visit was before) its price less any credit used. A visit in an opening balance received nothing here
// (its money is in the account). On a visit with no money fields: its price when paid, as before.
// Received means money that arrived (Astra r2, finding 10), the same rule Money uses on the server: the receipts taken
// on the visit (£24 taken on a £24 visit with £5 credit is £24 received, the £5 over kept as credit), never what was
// allocated; cash still waiting for its figure counts nothing yet. A visit marked paid with no receipt on it (every
// visit before brought money, and a bank tick) counts its price less any credit used, as before.
export function visitReceived(v: VisitMoney): number {
  if (v.carryover === 1 || v.state !== 'done') return 0
  const taken = Math.max(0, v.cash_taken_pence ?? 0)
  if (taken > 0) return taken
  if ((v.cash_unconfirmed ?? 0) > 0) return 0
  if (v.payment_status !== 'paid') return 0
  return Math.max(0, (v.amount_pence ?? 0) - fromCredit(v))
}

// The cash badge's words with brought money: "Paid cash, amount not recorded" while the owner has not confirmed it, or
// what was taken when it is not the price. null = the badge as before.
export function cashWords(v: VisitMoney, money: Fmt): string | null {
  if (v.payment_method !== 'cash' && !(v.cash_taken_pence ?? 0) && !(v.cash_unconfirmed ?? 0)) return null
  if ((v.cash_unconfirmed ?? 0) > 0) return 'Paid cash, amount not recorded'
  const taken = v.cash_taken_pence ?? 0
  if (taken > 0 && v.amount_pence !== null && taken !== v.amount_pence) return `${money(taken)} cash taken`
  return null
}

// ---- accounts ------------------------------------------------------------------------------------------------------
export interface BroughtCollection { id: number; account_id: number; amount_pence: number; on_date: string | null; provider?: string; opening_effect_pence: number; state?: string; version: number }
export interface BroughtLine { account_id: number; ext_id: string; kind: string; on_date: string | null; text: string; amount_pence: number | null; mandatory: number }
export interface BroughtAccount {
  id: number
  customer_id: number
  squeegee_ref: string
  opening_pence: number
  squeegee_pence: number
  as_of: string
  state: string
  chase_called_at?: string | null
  chase_count: number
  chased_on: string | null
  version: number
  left_pence: number
  chaseable_pence: number
  collecting_pence: number
  collecting_kept_owed_pence: number
  collections: BroughtCollection[]
  lines?: BroughtLine[]
  customer_name?: string
  customer_address?: string | null
  customer_phone?: string | null
  customer_email?: string | null
  customer_message_by?: string | null
  customer_version?: number
}
export interface UnconfirmedCash { id: number; customer_id?: number; clean_id: number | null; received_on: string; customer_name?: string; visit_pence?: number | null; visit_date?: string | null }
export interface OwedBrought { accounts: BroughtAccount[]; total_pence: number; count: number; collecting_pence: number; unconfirmed_cash: UnconfirmedCash[] }
export interface CustomerMoney { accounts: BroughtAccount[]; credit_pence: number; set_aside: { clean_id: number; pence: number; date: string | null }[]; unconfirmed_cash: UnconfirmedCash[] }
export interface RoundMoney { received_pence: number; outstanding_pence: number; owed_on_visits_pence: number; owed_on_accounts_pence: number; credit_pence: number; collecting_pence: number }

// "Collecting £22, don't chase" (Liam Q4).
export const collectingWords = (k: { amount_pence: number }, money: Fmt): string => `Collecting ${money(k.amount_pence)}, don't chase`

// The credit line on a customer's page: "£5 credit, set aside for the 14 Oct clean".
export function creditLine(m: CustomerMoney, shortDate: (iso: string) => string, money: Fmt): string | null {
  if (!m.credit_pence && !m.set_aside.length) return null
  const aside = m.set_aside.reduce((s, x) => s + x.pence, 0)
  const total = m.credit_pence + aside
  if (!total) return null
  const where = m.set_aside.find((x) => x.date)
  return where ? `${money(total)} credit, set aside for the ${shortDate(where.date as string)} clean` : `${money(total)} credit, for their next clean`
}

// A history line from Squeegee, as the customer page lists it.
export function lineWords(l: BroughtLine, money: Fmt): string {
  return l.amount_pence === null ? l.text : `${l.text} · ${l.amount_pence < 0 ? '-' : ''}${money(Math.abs(l.amount_pence))}`
}
